import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import {
  toMin, toHHMM, strToDate, todayStr, conceptColor, setEstadoCita, consultaConjuntaDeLaCita,
} from '../../lib/citasApi';

/**
 * AgendaGrid — primitiva compartida de la vista de agenda (día y semana).
 *
 * Props:
 *  - agenda:      agenda activa ({slot_minutes, working_hours, ...})
 *  - date:        "YYYY-MM-DD" (vista día) — alternativamente…
 *  - dates:       ["YYYY-MM-DD", ...] (vista semana; tiene prioridad sobre date)
 *  - citas:       citas a pintar (de cualquiera de los días visibles)
 *  - bloqueos:    bloqueos de agenda del rango visible
 *  - conceptos:   catálogo de conceptos (para colorear por concept_code)
 *  - onSlotClick: ({date, start}) al pulsar un hueco laborable vacío
 *  - onCitaClick: (cita, event) al pulsar una cita
 *  - compact:     escala reducida (vista semana)
 *  - isAdmin:     permite click fuera de horario/bloqueo (el backend valida con force)
 *  - showHeaders / onDateHeaderClick: cabeceras de columna clicables (semana)
 *  - agendas:     las agendas cargadas en la pantalla; con ellas una cita
 *                 prestada decide su «visto por enfermería» con SU agenda
 */

const GUTTER_PX = 56;

function hexToRgba(hex, alpha) {
  const h = String(hex || '').replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  if (full.length !== 6 || Number.isNaN(n)) return `rgba(46,110,94,${alpha})`;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

/** Índice de working_hours para una fecha (0 = lunes … 6 = domingo). */
function weekdayKey(dateStr) {
  return String((strToDate(dateStr).getDay() + 6) % 7);
}

function nowMinutes() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

/**
 * Coloca las citas a ANCHO COMPLETO, sin carriles laterales. Si dos citas se
 * solapan en el tiempo, la segunda se APILA justo debajo de la primera en vez
 * de estrecharse a un lado: el día "se expande" hacia abajo lo necesario y
 * todas las citas mantienen el mismo tamaño (2026-07-16, petición de la
 * clínica — al forzar/añadir un hueco nada se mueve a izquierda/derecha).
 *
 * Devuelve cada item con `top`/`height` ya calculados en píxeles.
 */
function layoutCitas(citas, pxPerMin, minStart, stack = true) {
  const items = citas
    .map((c) => {
      const start = toMin(c.start);
      const end = c.end ? toMin(c.end) : start + (c.duration_minutes || 15);
      return { cita: c, start, end: Math.max(end, start + 5), lane: 0, laneCount: 1 };
    })
    .sort((a, b) => a.start - b.start || b.end - a.end);

  if (!stack) {
    // PROPORCIONAL (vista semana / varias columnas): cada cita en su hora real,
    // alineada con la regla de horas fija de la izquierda.
    for (const it of items) {
      it.top = (it.start - minStart) * pxPerMin;
      it.height = Math.max(14, (it.end - it.start) * pxPerMin - 2);
    }
    return items;
  }

  // APILADO UNIFORME (vista de un solo día / agenda suelta): todas las citas del
  // MISMO tamaño mínimo (para que se lean nombre + nota), apiladas como lista.
  // La regla de horas se alinea a cada cita.
  //
  // Los RATOS LIBRES entre dos citas sí se dibujan a escala (2026-07-21), para
  // ver de un vistazo dónde cabe alguien. Se topan por arriba y por abajo: sin
  // suelo un hueco de 15 min no se vería, y sin techo una tarde libre dejaría
  // media pantalla en blanco y habría que hacer scroll para nada.
  const HUECO_MIN_MINUTOS = 10;   // por debajo de esto no merece la pena pintarlo
  const HUECO_PX_MIN = 26;
  const HUECO_PX_MAX = 150;
  const altoHueco = (minutos) =>
    Math.round(Math.min(HUECO_PX_MAX, Math.max(HUECO_PX_MIN, minutos * pxPerMin)));

  // Una cita «no acude» se dibuja en su hueco, tachada (2026-09-08,
  // punto 12), pero NO ocupa el tiempo: el rato libre se mide desde la última
  // cita activa, así el hueco se sigue ofreciendo para citar a otro paciente,
  // y encima de la tachada no se pinta ningún «libres» (sería contarlo dos veces).
  let prevBottom = null;
  let prevEnd = null;
  for (const it of items) {
    const h = Math.max(60, (it.end - it.start) * pxPerMin - 2);
    const noAcude = esNoAcude(it.cita);
    let top;
    if (prevBottom == null) {
      top = 0;
    } else {
      const libres = prevEnd != null && !noAcude ? it.start - prevEnd : 0;
      if (libres >= HUECO_MIN_MINUTOS) {
        // La comida sigue siendo un hueco especial (etiqueta y color propios),
        // pero ya no mide 42 px fijos: mide lo que dura, como el resto.
        const esComida = libres >= 60 && prevEnd <= 16 * 60 && it.start >= 13 * 60;
        const px = altoHueco(libres);
        it.huecoArriba = {
          minutos: libres,
          px,
          tipo: esComida ? 'comida' : 'libre',
          desde: prevEnd,
          hasta: it.start,
        };
        top = prevBottom + 2 + px;
      } else {
        top = prevBottom + 2;
      }
    }
    it.top = top;
    it.height = h;
    prevBottom = top + h;
    if (!noAcude) prevEnd = it.end;
  }
  return items;
}

/** Cita a la que el paciente no se presentó (status «no_acude»). */
function esNoAcude(cita) {
  return cita?.status === 'no_acude';
}

/** "45 min" · "1 h" · "1 h 30 min" */
function duracionLegible(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** "2026-05-12" -> "12/05/2026" sin depender de la zona horaria. */
function fechaCorta(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/**
 * Etiquetas rojas junto al nombre con las cirugías YA realizadas del paciente
 * (la última cirugía y, si hubo, el retoque posterior). Vienen calculadas del
 * backend en `cita.cirugias_previas`. Así en una revisión se ve al instante
 * que es un paciente operado y cuándo.
 */
function CirugiasPrevias({ cita }) {
  const previas = cita.cirugias_previas || [];
  if (!previas.length) return null;
  return (
    <>
      {previas.map((qx) => (
        <span
          key={`${qx.tipo}-${qx.date}`}
          className="ml-1.5 inline-flex items-center rounded px-1.5 py-[1px] text-[10px] font-bold uppercase tracking-wide bg-red-600 text-white leading-none align-middle whitespace-nowrap"
          data-testid="cita-cirugia-previa"
          title={`${qx.tipo} realizada el ${fechaCorta(qx.date)}`}
        >
          {qx.tipo} {fechaCorta(qx.date)}
        </span>
      ))}
    </>
  );
}

// Cita CONFIRMADA por el paciente desde el enlace del recordatorio
// (2026-09-03: «debe quedar marcada automáticamente en verde en la
// agenda como Confirmada»). Verde propio, distinto del de «Realizada».
const VERDE_CONFIRMADA = '#16A34A';
// Gris de la cita «no acude» (2026-09-08, punto 12): se queda en su
// hueco, tachada y apagada, sin el color del concepto.
const GRIS_NO_ACUDE = '#64748B';

// AGENDA BLOQUEADA EL DÍA ENTERO (2026-09-17): «icono de candado y
// toda la columna de la agenda en color rojo, que quede claro de un vistazo».
// Antes el día completo salía AZUL, que se confundía con una cirugía. El rojo
// intenso (#B91C1C) se usa en las clases de Tailwind —que necesitan el literal
// escrito— y este de aquí en los estilos en línea (tintes calculados).
const ROJO_BLOQUEO = '#DC2626';
// Y las citas que ya estaban dadas ANTES del bloqueo se apartan a este % del
// ancho de la columna, a la derecha: encima del rojo no se leían (mismo
// día). Va en porcentaje a propósito: mida lo que mida la columna, la
// cita ni se sale de ella ni pisa la de al lado.
const LATERAL_CITAS_PCT = 56;
// El rótulo con el candado y el MOTIVO va a ancho completo de la columna, no
// dentro del rectángulo rojo recortado: ahí se quedaba en el 44% de la
// izquierda y en vista semana (~190 px de columna) un motivo largo salía
// cortado y el bloqueo no decía por qué (2026-09-17). Como en vista de día las
// citas empiezan en el píxel 0, se les reserva esta banda de arriba para que el
// rótulo no las tape.
const BANDA_ROTULO_PX = 30;

// Flujo del día: color e inicial por estado del paciente.
const ESTADO_STYLES = {
  espera: { color: '#D97706', label: 'Sala de espera' },
  tratamiento: { color: '#2563EB', label: 'Consulta' },
  // Quirófano: postoperatorio ya explicado, puede pasar a cambiarse.
  postop_ok: { color: '#7C3AED', label: 'Listo para cambiarse' },
  realizada: { color: '#059669', label: 'Realizada' },
  // Quirófano: ya ha abandonado la clínica (realizada = en recuperación).
  se_fue: { color: '#64748B', label: 'Se fue' },
};

function CitaBlock({
  item, pxPerMin, minStart, color, onCitaClick,
  consultaConjunta = false, esQuirofano = false,
  // `bloqueada`: la agenda está bloqueada el día entero y esta cita venía de
  // antes del bloqueo. Se aparta al lateral y se pinta en rojo (2026-09-17).
  bloqueada = false,
}) {
  const { cita, start, end } = item;
  // top/height los calcula layoutCitas (apilado a ancho completo). Fallback por si acaso.
  const top = item.top != null ? item.top : (start - minStart) * pxPerMin;
  const height = item.height != null ? item.height : Math.max(14, (end - start) * pxPerMin - 2);
  const tiny = height < 36;
  // El check de enfermería solo aplica en agendas de consulta conjunta: la
  // de la CITA, que la calcula quien pinta el bloque (24/9/2026).
  const showVisto = !esQuirofano && consultaConjunta && cita.visto_enfermeria;
  // Cita prestada de otra agenda (solo en la agenda de enfermería).
  const origen = cita.agenda_origen || null;
  // «No acude» (2026-09-08, punto 12): la cita se queda en su hueco
  // con el nombre tachado y en gris, para que conste que estaba citada y el
  // paciente no se presentó. No lleva círculo de llegada ni verde de
  // confirmada, y va por debajo de una cita activa dada en ese mismo hueco.
  const noAcude = esNoAcude(cita);

  // Estado del paciente en el día (Sala de espera / Consulta / Realizada).
  // "Sala de espera" es el ÚNICO marcador de llegada: un clic en el círculo
  // (solo citas de HOY) lo activa/desactiva. Ya no hay código de luz aparte.
  const esHoy = cita.date === todayStr();
  const [estadoPac, setEstadoPac] = useState(cita.estado_paciente || null);
  const [savingEstado, setSavingEstado] = useState(false);
  useEffect(() => { setEstadoPac(cita.estado_paciente || null); }, [cita.estado_paciente]);
  // El circuito del día (sala de espera → …) vale para toda la agenda, no
  // solo quirófano (2026-07-28: una restricción de un par de días antes
  // había apagado sin querer la luz de llegada en las consultas normales,
  // que es donde más se usa).
  const estado = (esQuirofano && ['espera', 'tratamiento'].includes(estadoPac))
    ? null
    : ESTADO_STYLES[estadoPac] || null;
  const enEspera = !esQuirofano && estadoPac === 'espera';
  // Confirmada por el paciente: el bloque se pinta en verde mientras no haya
  // un estado del día (sala de espera, consulta, realizada…), que sigue
  // mandando — la confirmación importa ANTES de que el paciente llegue.
  const confirmada = cita.paciente_confirmacion === 'confirmada' && !estado && !noAcude;
  const tinte = noAcude
    ? hexToRgba(GRIS_NO_ACUDE, 0.10)
    : bloqueada
      ? hexToRgba(ROJO_BLOQUEO, 0.14)
      : estado
        ? hexToRgba(estado.color, 0.14)
        : confirmada ? hexToRgba(VERDE_CONFIRMADA, 0.2) : hexToRgba(color, 0.12);

  const toggleEspera = async (e) => {
    e.stopPropagation();
    if (savingEstado || !esHoy) return;
    const next = enEspera ? null : 'espera';
    const prev = estadoPac;
    setEstadoPac(next); // optimista
    setSavingEstado(true);
    try {
      await setEstadoCita(cita.id, next);
    } catch (err) {
      setEstadoPac(prev);
      toast.error(err?.response?.data?.detail || 'No se pudo marcar sala de espera');
    } finally {
      setSavingEstado(false);
    }
  };

  return (
    <button
      type="button"
      data-testid={`agenda-cita-${cita.id}`}
      data-confirmada={confirmada ? '1' : undefined}
      data-no-acude={noAcude ? '1' : undefined}
      data-bloqueada={bloqueada ? '1' : undefined}
      onClick={(e) => {
        e.stopPropagation();
        onCitaClick?.(cita, e);
      }}
      className={`absolute rounded-md border bg-white text-left overflow-hidden cursor-pointer shadow-sm hover:shadow-md hover:z-[12] transition-[box-shadow,transform] active:scale-[0.99] ${
        noAcude ? 'z-[8] opacity-70 border-dashed' : 'z-[10]'
      } ${cita.estado_paciente === 'realizada' ? 'opacity-60' : ''}`}
      style={{
        top,
        height,
        // Agenda bloqueada el día entero: la cita se aparta al lateral DERECHO
        // de su propia columna, para que el rojo del bloqueo no se la coma
        // (2026-09-17). El resto de días, a ancho completo de siempre.
        left: bloqueada ? `${100 - LATERAL_CITAS_PCT}%` : 2,
        right: 2,
        borderLeft: `3px solid ${noAcude ? GRIS_NO_ACUDE : bloqueada ? ROJO_BLOQUEO : color}`,
        borderColor: noAcude
          ? hexToRgba(GRIS_NO_ACUDE, 0.6)
          : bloqueada
            ? hexToRgba(ROJO_BLOQUEO, 0.75)
            : estado
              ? hexToRgba(estado.color, 0.55)
              : confirmada ? hexToRgba(VERDE_CONFIRMADA, 0.6) : 'hsl(var(--border))',
        borderLeftColor: noAcude ? GRIS_NO_ACUDE : bloqueada ? ROJO_BLOQUEO : color,
        // Base de tarjeta (clara/oscura según el tema) + tinte de color encima:
        // la cita se lee bien tanto en claro como en oscuro (2026-07-17).
        background: `linear-gradient(${tinte}, ${tinte}), hsl(var(--card))`,
      }}
      title={[
        `${cita.start}–${cita.end || ''} · ${cita.patient_name || ''} · ${cita.concept_name || ''}`,
        cita.procedimiento || '',
        cita.notes || '',
        noAcude
          ? `✗ No acudió${cita.no_acude_by_name ? ` · marcado por ${cita.no_acude_by_name}` : ''}`
          : cita.paciente_confirmacion === 'confirmada' ? '✓ Confirmada por el paciente' : '',
        bloqueada ? 'Agenda bloqueada ese día — cita dada antes del bloqueo' : '',
        showVisto ? '✓ Visto por enfermería' : '',
        estado ? `● ${estado.label}` : '',
      ].filter(Boolean).join('\n')}
    >
      {/* Sala de espera: un clic marca/desmarca (citas de HOY, cualquier agenda).
          En una «no acude» no tiene sentido: no hay llegada que marcar. */}
      {!esQuirofano && !noAcude && (enEspera || esHoy) && (
        <span
          role="button"
          tabIndex={-1}
          onClick={toggleEspera}
          title={enEspera
            ? `En sala de espera${esHoy ? ' — pulsa para quitar' : ''}`
            : 'Pulsa cuando el paciente llegue (sala de espera)'}
          data-testid={`cita-espera-${cita.id}`}
          className={`absolute top-0.5 right-0.5 z-[13] inline-block w-3.5 h-3.5 rounded-full transition-colors ${
            enEspera
              ? 'bg-amber-500 shadow-[0_0_4px_rgba(217,119,6,0.7)]'
              : 'bg-white border-2 border-slate-300 hover:border-amber-400'
          } ${esHoy ? 'cursor-pointer' : 'cursor-default'} ${savingEstado ? 'opacity-50' : ''}`}
        />
      )}

      {/* Consulta conjunta: ya visto por enfermería → check verde */}
      {showVisto && (
        <span
          className="absolute top-0.5 right-5 z-[11] inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-emerald-500 text-white text-[9px] leading-none"
          data-testid="cita-visto-enfermeria-badge"
          aria-label="Visto por enfermería"
        >
          ✓
        </span>
      )}
      {/* Aviso de documentación pendiente (LOPD sin firmar / consentimiento
          sin firmar). lopd_ok===false → el paciente no tiene LOPD. */}
      {(cita.lopd_ok === false || cita.consent_pendiente) && !tiny && (
        <span
          className="absolute top-0.5 left-1 z-[13] inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-500 text-white text-[9px] leading-none shadow"
          data-testid="cita-aviso-docs"
          title={[
            cita.lopd_ok === false ? 'LOPD pendiente' : null,
            cita.consent_pendiente ? 'Consentimiento sin firmar' : null,
          ].filter(Boolean).join(' · ')}
        >
          ⚠
        </span>
      )}
      {tiny ? (
        <div className="px-1.5 leading-[16px] text-[13.5px] truncate">
          {(cita.lopd_ok === false || cita.consent_pendiente) && (
            <span
              className="mr-1 align-middle text-amber-600"
              title={[
                cita.lopd_ok === false ? 'LOPD pendiente' : null,
                cita.consent_pendiente ? 'Consentimiento sin firmar' : null,
              ].filter(Boolean).join(' · ')}
            >⚠</span>
          )}
          {estado && (
            <span
              className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle"
              style={{ backgroundColor: estado.color }}
              aria-label={estado.label}
            />
          )}
          {confirmada && (
            <span
              className="mr-1 align-middle font-bold text-[#16A34A]"
              data-testid="cita-confirmada-badge"
              title="Confirmada por el paciente"
            >✓</span>
          )}
          {bloqueada && (
            <Lock className="inline-block w-3 h-3 mr-0.5 align-[-1px] text-[#B91C1C]" aria-hidden="true" />
          )}
          <span className={`tabular-nums ${bloqueada ? 'font-semibold text-[#B91C1C]' : 'text-[#13231E]'}`}>{cita.start}</span>{' '}
          <span
            className={`font-semibold ${noAcude ? 'line-through text-[#13231E]/60' : 'text-[#13231E]'}`}
            data-testid={`cita-nombre-${cita.id}`}
          >{cita.patient_name}</span>
          {noAcude && (
            <span className="ml-1 font-semibold text-amber-700" data-testid={`cita-no-acude-${cita.id}`}>· No acude</span>
          )}
          <CirugiasPrevias cita={cita} />
          {cita.notes ? <span className="text-muted-foreground italic"> · {cita.notes}</span> : null}
        </div>
      ) : (
        <div className="px-1.5 py-0.5 min-w-0">
          <div className="flex items-center justify-between gap-1 leading-tight">
            <span className={`text-[10px] tabular-nums truncate ${
              bloqueada ? 'font-semibold text-[#B91C1C]' : 'text-muted-foreground'
            }`}>
              {bloqueada && (
                <Lock className="inline-block w-3 h-3 mr-0.5 align-[-1px]" aria-hidden="true" />
              )}
              {cita.start}–{cita.end || toHHMM(end)}
              {estado && !enEspera && estadoPac !== 'postop_ok' && (
                <span className="font-medium not-italic" style={{ color: estado.color }}>
                  {' '}· {estado.label}
                </span>
              )}
              {noAcude && (
                <span className="font-semibold not-italic text-amber-700" data-testid={`cita-no-acude-${cita.id}`}>
                  {' '}· No acude
                </span>
              )}
            </span>
            {/* La etiqueta de cirugía vive AQUÍ, en el hueco libre de la línea
                de la hora: así no roba sitio al nombre ni a las notas. La de
                «Confirmada» también: en línea, sin añadir altura al bloque. */}
            <span className="shrink-0 inline-flex items-center gap-1">
              {confirmada && (
                <span
                  className="inline-flex items-center rounded px-1.5 py-[1px] text-[10px] font-bold uppercase tracking-wide bg-[#16A34A] text-white leading-none whitespace-nowrap"
                  data-testid="cita-confirmada-badge"
                  title="Confirmada por el paciente desde el enlace del recordatorio"
                >
                  ✓ Confirmada
                </span>
              )}
              <CirugiasPrevias cita={cita} />
            </span>
          </div>
          {enEspera && (
            <div className="mb-0.5">
              <span className="inline-flex items-center gap-1 rounded px-1.5 py-[1px] text-[10px] font-bold uppercase tracking-wide bg-amber-500 text-white leading-none">
                ● Sala de espera
              </span>
            </div>
          )}
          {/* Aviso de quirófano: postoperatorio explicado, puede pasar a
              cambiarse. Tan visible como el de sala de espera; llega solo con
              el auto-refresco de la agenda. */}
          {estadoPac === 'postop_ok' && (
            <div className="mb-0.5">
              <span className="inline-flex items-center gap-1 rounded px-1.5 py-[1px] text-[10px] font-bold uppercase tracking-wide bg-[#7C3AED] text-white leading-none" data-testid="cita-listo-cambiarse">
                ● Listo para cambiarse
              </span>
            </div>
          )}
          <div
            className={`text-[15px] font-semibold leading-tight truncate ${
              noAcude ? 'line-through text-[#13231E]/60' : 'text-[#13231E]'
            }`}
            data-testid={`cita-nombre-${cita.id}`}
          >{cita.patient_name}</div>
          {cita.notes && (
            <div
              className="text-[12px] text-[#13231E]/80 italic leading-tight overflow-hidden"
              style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
            >{cita.notes}</div>
          )}
          <div className="text-[10px] text-muted-foreground leading-tight truncate">
            {[cita.procedimiento, cita.cirujano, cita.concept_name, cita.lugar,
              origen ? `↗ ${origen}` : null].filter(Boolean).join(' · ')}
          </div>
        </div>
      )}
    </button>
  );
}

function BloqueoBlock({ bloqueo, minStart, maxEnd, pxPerMin, citasAlLateral = false }) {
  const from = bloqueo.all_day ? minStart : Math.max(minStart, toMin(bloqueo.start));
  const to = bloqueo.all_day ? maxEnd : Math.min(maxEnd, toMin(bloqueo.end));
  if (to <= from) return null;
  // Día COMPLETO bloqueado: candado y la COLUMNA ENTERA en rojo
  // (2026-09-17). Antes salía azul —y solo cubría el rango horario, así que si el
  // día se estiraba al apilar citas el azul se quedaba a medias—: ahora va de
  // arriba abajo de la columna (top/bottom 0), mida lo que mida.
  // Si ese día quedaban citas de ANTES del bloqueo, el rojo deja libre el
  // lateral derecho y ahí se apartan ellas: encima del rojo no se leían.
  // Los bloqueos de TRAMO horario conservan el rayado rojo de siempre.
  if (bloqueo.all_day) {
    return (
      <>
        <div
          className="absolute z-[5] pointer-events-none rounded-sm border border-[#B91C1C]/50 overflow-hidden"
          data-testid={`agenda-bloqueo-${bloqueo.id}`}
          data-dia-completo="1"
          style={{
            top: 0,
            bottom: 0,
            left: 0,
            right: citasAlLateral ? `${LATERAL_CITAS_PCT}%` : 0,
            // Rojo TRANSLÚCIDO, no sólido: se posa sobre el fondo de la columna
            // (que ya va teñido) y sirve igual en claro y en oscuro.
            backgroundColor: hexToRgba(ROJO_BLOQUEO, 0.22),
          }}
        />
        {/* El motivo, a ANCHO COMPLETO de la columna y fuera del rectángulo
            recortado (2026-09-17): dentro vivía en el 44% de la izquierda y en
            vista semana se cortaba justo la parte que importa, el porqué.
            Rojo oscuro con texto blanco: se lee bien encima del tinte rojo. */}
        <div
          className="absolute z-[7] px-1 pt-1 pointer-events-none"
          data-testid={`agenda-bloqueo-rotulo-${bloqueo.id}`}
          style={{ top: 0, left: 0, right: 0 }}
        >
          <div className="flex items-center justify-center gap-1.5 rounded-md bg-[#B91C1C] text-white text-[11px] font-semibold px-2 py-1 shadow-sm">
            <Lock className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            <span className="truncate">{bloqueo.reason || 'Agenda bloqueada'}</span>
          </div>
        </div>
      </>
    );
  }
  return (
    <div
      className="absolute inset-x-0 z-[5] pointer-events-none rounded-sm border border-red-200 overflow-hidden"
      data-testid={`agenda-bloqueo-${bloqueo.id}`}
      style={{
        top: (from - minStart) * pxPerMin,
        height: (to - from) * pxPerMin,
        background:
          'repeating-linear-gradient(45deg, rgba(220,38,38,0.08) 0px, rgba(220,38,38,0.08) 6px, rgba(220,38,38,0.03) 6px, rgba(220,38,38,0.03) 12px)',
      }}
    >
      <div className="px-1.5 py-0.5 text-[10px] text-red-700/90 truncate">
        <Lock className="inline-block w-3 h-3 mr-1 align-[-2px]" aria-hidden="true" />
        {bloqueo.reason || 'Bloqueado'}
      </div>
    </div>
  );
}

export default function AgendaGrid({
  agenda,
  date,
  dates,
  citas = [],
  bloqueos = [],
  conceptos = [],
  onSlotClick,
  onCitaClick,
  compact = false,
  isAdmin = false,
  showHeaders = false,
  onDateHeaderClick,
  // Las agendas que tiene cargadas la pantalla: una cita prestada (en la de
  // Enfermería) decide su «visto por enfermería» con SU agenda (24/9/2026).
  agendas = [],
}) {
  const cols = dates && dates.length > 0 ? dates : date ? [date] : [];
  const pxPerMin = compact ? 1.5 : 2.2;
  // Una sola columna (día / agenda suelta) → citas del MISMO tamaño apiladas y
  // la regla de horas alineada a cada cita. Varias columnas (semana) →
  // proporcional, alineado a la regla de horas fija.
  const stack = cols.length === 1;
  const today = todayStr();

  // Línea "ahora": refresco cada minuto solo si algún día visible es hoy.
  const hasToday = cols.includes(today);
  const [nowMin, setNowMin] = useState(() => nowMinutes());
  useEffect(() => {
    if (!hasToday) return undefined;
    const id = setInterval(() => setNowMin(nowMinutes()), 60000);
    return () => clearInterval(id);
  }, [hasToday]);

  const conceptByCode = useMemo(() => {
    const map = {};
    for (const c of conceptos) map[c.code] = c;
    return map;
  }, [conceptos]);

  const bloqueosFor = (dstr) => bloqueos.filter((b) => b.date_from <= dstr && dstr <= b.date_to);
  // Cierres bloquean huecos; aperturas añaden tramos extraordinarios de citas.
  const cierresFor = (dstr) => bloqueosFor(dstr).filter((b) => (b.tipo || 'cierre') !== 'apertura');
  const aperturasFor = (dstr) =>
    bloqueosFor(dstr).filter((b) => (b.tipo || 'cierre') === 'apertura' && b.start && b.end);
  const rangesFor = (dstr) => [
    ...(agenda?.working_hours?.[weekdayKey(dstr)] || []),
    ...aperturasFor(dstr).map((a) => ({ start: a.start, end: a.end })),
  ];

  // Rango visual: primera hora laborable −30 min … última +30 min (fallback 09–21).
  const { minStart, maxEnd } = useMemo(() => {
    let lo = Infinity;
    let hi = -Infinity;
    for (const d of cols) {
      for (const r of rangesFor(d)) {
        lo = Math.min(lo, toMin(r.start));
        hi = Math.max(hi, toMin(r.end));
      }
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) {
      lo = 9 * 60;
      hi = 21 * 60;
    }
    return { minStart: Math.max(0, lo - 30), maxEnd: Math.min(24 * 60, hi + 30) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agenda, bloqueos, cols.join('|')]);

  const totalPx = (maxEnd - minStart) * pxPerMin;

  // Marcas cada 30 min (la primera alineada a múltiplo de 30).
  const ticks = useMemo(() => {
    const out = [];
    for (let m = Math.ceil(minStart / 30) * 30; m <= maxEnd; m += 30) out.push(m);
    return out;
  }, [minStart, maxEnd]);

  // Layout de cada día (apilado a ancho completo). Se calcula una vez para
  // saber cuánto "se expande" el día y ajustar la altura total de la rejilla.
  const laidOutByCol = useMemo(() => {
    const map = {};
    for (const d of cols) {
      // Las anuladas no se pintan; las «no acude» sí, en su hueco y tachadas
      // (2026-09-08, punto 12): constancia de que estaban citadas.
      const dayCitas = citas.filter(
        (c) => c.date === d && !['cancelada', 'cancelled'].includes(c.status),
      );
      const items = layoutCitas(dayCitas, pxPerMin, minStart, stack);
      // Día bloqueado entero en vista de DÍA: las citas empiezan en el píxel 0,
      // así que bajan una banda para dejar sitio al rótulo del motivo, que va a
      // ancho completo arriba del todo (2026-09-17). Bajándolas AQUÍ, la regla
      // de horas, las líneas y el alto de la rejilla las siguen solas. En vista
      // semana no se toca nada: cada cita tiene que quedarse en su hora.
      if (stack && items.length > 0 && cierresFor(d).some((b) => b.all_day)) {
        for (const it of items) it.top += BANDA_ROTULO_PX;
      }
      map[d] = items;
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cols.join('|'), citas, bloqueos, pxPerMin, minStart, stack]);

  // La rejilla mide lo normal (rango horario) o más si algún día se expandió
  // al apilar citas solapadas — así el gutter y las columnas van a la par.
  const gridHeight = useMemo(() => {
    let contentBottom = 0;
    for (const d of cols) {
      for (const it of laidOutByCol[d] || []) contentBottom = Math.max(contentBottom, it.top + it.height + 8);
    }
    // Apilado (lista): solo lo que ocupan las citas. Proporcional: el rango
    // horario completo (o más si algo se expandió).
    if (stack) return Math.max(contentBottom, 120);
    return Math.max(totalPx, contentBottom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laidOutByCol, totalPx, cols.join('|'), stack]);

  const handleColumnClick = (e, dstr) => {
    if (!onSlotClick) return;
    // AGENDA BLOQUEADA EL DÍA ENTERO: no se cita encima, y no se cita ni siendo
    // admin (2026-09-17: «no debe permitir citar nada sobre ella»).
    // Antes el clic pasaba para todo el que puede forzar huecos —dirección,
    // mostrador, enfermería, doctor— y el único freno llegaba al guardar, con
    // un 409 y un botón «Forzar solapamiento» que suena a doble cita, no a «ese
    // día no hay nadie en la clínica»: el paciente se plantaba aquí un día de
    // congreso o de vacaciones. Si de verdad hay que citar ese día, primero se
    // quita el bloqueo, que es un gesto consciente y queda registrado.
    // Los bloqueos de TRAMO horario no entran aquí: esos sí tiene sentido
    // forzarlos, y siguen con la regla de siempre (solo quien puede forzar).
    const bloqueoDiaCompleto = cierresFor(dstr).find((b) => b.all_day);
    if (bloqueoDiaCompleto) {
      toast.error(`Agenda bloqueada: ${bloqueoDiaCompleto.reason || 'sin motivo'}`, {
        description: 'Ese día no se pueden dar citas. Si hace falta, quita antes el bloqueo.',
      });
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const rawMin = minStart + (e.clientY - rect.top) / pxPerMin;
    const slot = agenda?.slot_minutes || 15;
    const snapped = Math.floor(rawMin / slot) * slot;
    if (snapped < 0 || snapped >= 24 * 60) return;
    const inWorking = rangesFor(dstr).some((r) => snapped >= toMin(r.start) && snapped < toMin(r.end));
    const blocked = cierresFor(dstr).some(
      (b) => b.all_day || (snapped >= toMin(b.start) && snapped < toMin(b.end)),
    );
    // Fuera de horario o bloqueado: solo admin (el backend validará con force).
    if ((!inWorking || blocked) && !isAdmin) return;
    onSlotClick({ date: dstr, start: toHHMM(snapped) });
  };

  const headerLabel = (dstr) => {
    const d = strToDate(dstr);
    const wd = d.toLocaleDateString('es-ES', { weekday: 'short' }).replace('.', '');
    return `${wd.charAt(0).toUpperCase()}${wd.slice(1)} ${d.getDate()}`;
  };

  if (cols.length === 0 || !agenda) return null;

  const colMinWidth = compact ? 148 : 260;

  return (
    <div className="overflow-x-auto rounded-xl border border-[#D9E3DD] bg-white" data-testid="agenda-grid">
      <div style={{ minWidth: GUTTER_PX + cols.length * colMinWidth }}>
        {showHeaders && (
          <div className="flex border-b border-[#D9E3DD] bg-white">
            <div className="shrink-0 sticky left-0 z-[25] bg-white" style={{ width: GUTTER_PX }} />
            {cols.map((dstr) => (
              <button
                key={dstr}
                type="button"
                onClick={() => onDateHeaderClick?.(dstr)}
                data-testid={`agenda-col-header-${dstr}`}
                className={`flex-1 min-w-0 py-2 text-center text-xs font-medium border-l border-[#F0F6F2] transition-colors hover:bg-[rgba(46,110,94,0.08)] ${
                  dstr === today ? 'text-[#13231E]' : 'text-muted-foreground'
                }`}
                style={{ minWidth: colMinWidth }}
              >
                <span
                  className={
                    dstr === today
                      ? 'inline-flex items-center px-2 py-0.5 rounded-full bg-[#2E6E5E] text-white'
                      : undefined
                  }
                >
                  {headerLabel(dstr)}
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="flex">
          {/* Gutter de horas (sticky en scroll horizontal) */}
          <div
            className="shrink-0 sticky left-0 z-[25] bg-white border-r border-[#D9E3DD]"
            style={{ width: GUTTER_PX, height: gridHeight }}
          >
            <div className="relative h-full">
              {stack ? (
                /* Apilado: la hora de CADA cita, alineada a su bloque (las horas
                   "se expanden" siguiendo a las citas; nunca desalineadas). */
                (laidOutByCol[cols[0]] || []).map((it) => (
                  <span
                    key={it.cita.id}
                    className="absolute right-1.5 tabular-nums text-[10px] font-medium text-[#13231E]/80"
                    style={{ top: it.top + 3 }}
                  >
                    {it.cita.start}
                  </span>
                ))
              ) : (
                ticks.map((m) => (
                  <span
                    key={m}
                    className={`absolute right-1.5 -translate-y-1/2 tabular-nums ${
                      m % 60 === 0 ? 'text-[10px] font-medium text-[#13231E]/80' : 'text-[9px] text-muted-foreground/70'
                    }`}
                    style={{ top: (m - minStart) * pxPerMin }}
                  >
                    {toHHMM(m)}
                  </span>
                ))
              )}
            </div>
          </div>

          {/* Columnas de día */}
          {cols.map((dstr) => {
            const laidOut = laidOutByCol[dstr] || [];
            const dayCierres = cierresFor(dstr);
            const dayAperturas = aperturasFor(dstr);
            const isToday = dstr === today;
            // Bloqueo de DÍA COMPLETO: manda sobre el aspecto de toda la
            // columna (rojo + candado). Los de tramo horario NO: esos siguen
            // pintándose solo en su franja (2026-09-17).
            const bloqueoDia = dayCierres.find((b) => b.all_day) || null;
            // El lateral solo se reserva si de verdad hay citas que enseñar:
            // un día bloqueado y vacío luce el rojo entero, sin recortes.
            const citasAlLateral = !!bloqueoDia && laidOut.length > 0;
            return (
              <div
                key={dstr}
                data-testid={`agenda-col-${dstr}`}
                data-bloqueada={bloqueoDia ? '1' : undefined}
                title={bloqueoDia ? `Agenda bloqueada: ${bloqueoDia.reason || 'sin motivo'}` : undefined}
                onClick={(e) => handleColumnClick(e, dstr)}
                className={`relative flex-1 min-w-0 border-l first:border-l-0 ${
                  bloqueoDia ? 'border-l-[#B91C1C]/40' : 'border-l-[#F0F6F2]'
                }`}
                style={{
                  minWidth: colMinWidth,
                  height: gridHeight,
                  // Día bloqueado entero: la columna va en rojo de arriba abajo
                  // (2026-09-17). Si no, apilado (lista de un día): fondo de
                  // tarjeta (blanco en claro, oscuro en modo oscuro);
                  // proporcional (semana): fondo gris rayado.
                  ...(bloqueoDia
                    ? { backgroundColor: hexToRgba(ROJO_BLOQUEO, 0.12) }
                    : stack
                    ? { backgroundColor: 'hsl(var(--card))' }
                    : {
                        backgroundColor: 'hsl(var(--muted))',
                        backgroundImage:
                          'repeating-linear-gradient(45deg, rgba(46,110,94,0.045) 0px, rgba(46,110,94,0.045) 5px, transparent 5px, transparent 11px)',
                      }),
                }}
              >
                {/* Franjas laborables en blanco (solo en proporcional/semana).
                    Si el día está bloqueado entero no se pintan: taparían de
                    blanco el rojo del bloqueo (2026-09-17). */}
                {!stack && !bloqueoDia && rangesFor(dstr).map((r, i) => (
                  <div
                    key={i}
                    className="absolute inset-x-0 bg-white"
                    style={{
                      top: (toMin(r.start) - minStart) * pxPerMin,
                      height: (toMin(r.end) - toMin(r.start)) * pxPerMin,
                    }}
                  />
                ))}

                {/* Aperturas extraordinarias: tinte verde suave + etiqueta */}
                {dayAperturas.map((a) => (
                  <div
                    key={a.id}
                    className="absolute inset-x-0 z-[4] pointer-events-none border border-emerald-200 rounded-sm"
                    data-testid={`agenda-apertura-${a.id}`}
                    style={{
                      top: (toMin(a.start) - minStart) * pxPerMin,
                      height: (toMin(a.end) - toMin(a.start)) * pxPerMin,
                      backgroundColor: 'rgba(16,185,129,0.05)',
                    }}
                  >
                    <div className="px-1.5 py-0.5 text-[10px] text-emerald-700/90 truncate">
                      <span aria-hidden="true">➕</span> {a.reason || 'Huecos extra'}
                    </div>
                  </div>
                ))}

                {/* Líneas horizontales: en apilado, separan cada cita (alineadas
                    a los bloques); en proporcional, cada 30 min. */}
                {stack
                  ? laidOut.map((it) => (
                      <div
                        key={it.cita.id}
                        className="absolute inset-x-0 border-t border-[#F0F6F2]"
                        style={{ top: it.top }}
                      />
                    ))
                  : ticks.map((m) => (
                      <div
                        key={m}
                        className={`absolute inset-x-0 border-t ${m % 60 === 0 ? 'border-[#D9E3DD]' : 'border-[#F0F6F2]'}`}
                        style={{ top: (m - minStart) * pxPerMin }}
                      />
                    ))}

                {/* Cierres (encima del fondo, debajo de citas) */}
                {dayCierres.map((b) => (
                  <BloqueoBlock
                    key={b.id}
                    bloqueo={b}
                    minStart={minStart}
                    maxEnd={maxEnd}
                    pxPerMin={pxPerMin}
                    citasAlLateral={citasAlLateral}
                  />
                ))}

                {/* Ratos libres entre citas, a escala. El de mediodía se
                    etiqueta como Comida; el resto invita a citar en ese hueco. */}
                {/* Con la agenda bloqueada el día entero no se ofrece ningún
                    rato libre: invitaría a citar justo donde no se puede
                    (2026-09-17: «no debe permitir citar nada»). */}
                {stack && !bloqueoDia && laidOut.filter((it) => it.huecoArriba).map((it) => {
                  const g = it.huecoArriba;
                  const esComida = g.tipo === 'comida';
                  return (
                    <div
                      key={`hueco-${it.cita.id}`}
                      className="absolute inset-x-0 z-[6] flex items-center justify-center"
                      style={{ top: it.top - g.px, height: g.px }}
                      data-testid={esComida ? 'agenda-comida-gap' : 'agenda-hueco-libre'}
                    >
                      {esComida ? (
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#C97D1E] bg-[#FBF3E7] px-2.5 py-0.5 rounded-full border border-[#EBD7B5] pointer-events-none">
                          🍽 Comida · {duracionLegible(g.minutos)}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onSlotClick?.({ date: dstr, start: toHHMM(g.desde) }); }}
                          title={`Libre de ${toHHMM(g.desde)} a ${toHHMM(g.hasta)} — pulsa para citar`}
                          className="w-full h-full flex items-center justify-center group"
                        >
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground bg-transparent px-2 py-0.5 rounded-full border border-dashed border-border group-hover:border-[#2E6E5E] group-hover:text-[#2E6E5E] transition-colors">
                            {duracionLegible(g.minutos)} libres
                          </span>
                        </button>
                      )}
                    </div>
                  );
                })}

                {/* Citas */}
                {laidOut.map((item) => (
                  <CitaBlock
                    key={item.cita.id}
                    item={item}
                    pxPerMin={pxPerMin}
                    minStart={minStart}
                    color={conceptColor(conceptByCode[item.cita.concept_code])}
                    onCitaClick={onCitaClick}
                    consultaConjunta={consultaConjuntaDeLaCita(item.cita, [agenda, ...agendas])}
                    esQuirofano={(agenda?.tipo || agenda?.type) === 'quirofano'}
                    bloqueada={!!bloqueoDia}
                  />
                ))}

                {/* Línea "ahora" (solo en proporcional; en apilado no aplica) */}
                {!stack && isToday && nowMin >= minStart && nowMin <= maxEnd && (
                  <div
                    className="absolute inset-x-0 z-[20] pointer-events-none"
                    data-testid="agenda-now-line"
                    style={{ top: (nowMin - minStart) * pxPerMin }}
                  >
                    <div className="border-t border-red-500" />
                    <div className="absolute -left-[3px] -top-[3px] w-1.5 h-1.5 rounded-full bg-red-500" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
